import React, {
  Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import {
  createV2Flow,
  deleteV2Flow,
  getV2Flow,
  getV2WorkflowBuilderPrompt,
  studioV2NewFlowDraft,
  updateV2Flow,
} from '../../api/studioV2.api';
import StudioPageShell from '../../components/studio/StudioPageShell';
import StudioV2FlowEdgesTable from '../../components/studio/StudioV2FlowEdgesTable';
import StudioV2FlowStateEditor from '../../components/studio/StudioV2FlowStateEditor';
import StudioV2FlowSettingsEditor from '../../components/studio/StudioV2FlowSettingsEditor';
import StudioV2FlowMcpEditor from '../../components/studio/StudioV2FlowMcpEditor';
import StudioV2FlowStateSchemaEditor from '../../components/studio/StudioV2FlowStateSchemaEditor';
import StudioV2IntegrationCatalog from '../../components/studio/StudioV2IntegrationCatalog';
import StudioV2ToolCatalog from '../../components/studio/StudioV2ToolCatalog';
import FlowTriggersPanel from '../../components/studio/flowBuilder/FlowTriggersPanel';
import { mergeTemplateIntoCurrentFlow, STUDIO_V2_FLOW_TEMPLATES } from '../../components/studio/studioV2FlowTemplates';
import { normalizeV2FlowEdge, sanitizeFlowDefinitionV2Edges } from '../../components/studio/studioV2FlowModel';
import { legacyFlowToV2Client } from '../../utils/legacyFlowToV2Client';
import { touchStudioRecent } from '../../utils/studioRecents';
import StudioV2JsonMonacoEditor from '../../components/studio/StudioV2JsonMonacoEditor';
import AppReadinessButton from '../../components/studio/appBuilder/AppReadinessButton';
import FlowTestPanel from '../../components/studio/flowBuilder/FlowTestPanel';
import { FRIENDLY_FLOW_TEMPLATES } from '../../components/studio/flowBuilder/NewFlowWizard';
import { flowNameOf, flowReadiness, parseFlowText } from '../../components/studio/flowBuilder/flowBuilderModel';
import { aiStepProblems, useAiStatus } from '../../components/studio/flowBuilder/aiModels';
import FlowAppsButton from '../../components/studio/flowBuilder/FlowAppsButton';

const StudioV2FlowCanvas = lazy(() => import('../../components/studio/StudioV2FlowCanvas'));
const StudioV2FlowAiChatPanel = lazy(() => import('../../components/studio/StudioV2FlowAiChatPanel'));

const ADVANCED_STORAGE = 'studio_flow_builder_advanced_v1';
const ADVANCED_TABS = ['edges', 'state', 'schema', 'json'];

const STATUS_CHIP = {
  published: { label: 'Ready', color: 'success' },
  draft: { label: 'Draft', color: 'warning' },
  archived: { label: 'Archived', color: 'default' },
};

function Loading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <CircularProgress size={36} />
    </Box>
  );
}

function Section({ title, description, children }) {
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{description}</Typography>
      {children}
    </Box>
  );
}

function StudioFlowEditPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [tab, setTab] = useState('build');
  const [advanced, setAdvanced] = useState(() => {
    try {
      return window.localStorage.getItem(ADVANCED_STORAGE) === '1';
    } catch {
      return false;
    }
  });
  const [testMounted, setTestMounted] = useState(false);
  const [graphEpoch, setGraphEpoch] = useState(0);
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantPrompt, setAssistantPrompt] = useState('');
  const [aiPromptOpen, setAiPromptOpen] = useState(false);
  const [aiPromptText, setAiPromptText] = useState('');
  const [aiPromptBusy, setAiPromptBusy] = useState(false);
  const [aiPromptError, setAiPromptError] = useState(null);
  const [dupBusy, setDupBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [lastSavedText, setLastSavedText] = useState('');
  const [accessRole, setAccessRole] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const importRef = useRef(null);
  const loadedRef = useRef(false);
  const autoSaveTimerRef = useRef(null);
  const savingRef = useRef(false);
  const textRef = useRef('');
  const lastSavedRef = useRef('');
  textRef.current = text;
  lastSavedRef.current = lastSavedText;
  const readOnly = accessRole === 'viewer';
  const dirty = Boolean(text) && text !== lastSavedText;

  const def = useMemo(() => parseFlowText(text), [text]);
  const aiStatus = useAiStatus();
  const readiness = useMemo(
    () => flowReadiness(def, { aiProblems: aiStatus ? aiStepProblems(def, aiStatus) : undefined }),
    [def, aiStatus],
  );
  const debugProfileId = useMemo(() => new URLSearchParams(location.search).get('debugProfileId') || '', [location.search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { flow, error: err } = await getV2Flow(id);
      if (cancelled) return;
      if (err) {
        setError(err);
        setLoading(false);
        return;
      }
      const loaded = sanitizeFlowDefinitionV2Edges(flow?.definition || {});
      const serialized = JSON.stringify(loaded, null, 2);
      setText(serialized);
      setLastSavedText(serialized);
      setAccessRole(flow?.accessRole || 'owner');
      setSaveError(null);
      loadedRef.current = false;
      touchStudioRecent({
        kind: 'flow',
        id: String(id),
        name: loaded?.name || flow?.name || id,
        href: `/studio/flows/${id}`,
      });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (debugProfileId) {
      setTestMounted(true);
      setTab('test');
    }
  }, [debugProfileId]);

  useEffect(() => {
    const prompt = location.state?.assistantPrompt;
    if (!prompt) return;
    setAssistantPrompt(String(prompt));
    setAssistantOpen(true);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistFlow = useCallback(async ({ silent = false } = {}) => {
    const sentText = textRef.current;
    if (!sentText || savingRef.current) return false;
    let parsed;
    try {
      parsed = JSON.parse(sentText);
    } catch (e) {
      setSaveError(`Not saved: the flow has a formatting problem (${e.message}). Fix it in the JSON view.`);
      return false;
    }
    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.edges)) {
      parsed = { ...parsed, edges: parsed.edges.map((e) => normalizeV2FlowEdge(e)) };
    }
    savingRef.current = true;
    setSaving(true);
    setError(null);
    const { error: err } = await updateV2Flow(id, parsed);
    savingRef.current = false;
    setSaving(false);
    if (err) {
      setSaveError(`Not saved: ${err}`);
      return false;
    }
    setSaveError(null);
    const serialized = JSON.stringify(parsed, null, 2);
    setLastSavedText(serialized);
    lastSavedRef.current = serialized;
    // Keep edits made while the request was in flight; autosave picks them up next.
    if (textRef.current === sentText) setText(serialized);
    if (!silent) setToast('Saved');
    return true;
  }, [id]);

  /** Saves pending edits right away (tests run the saved version). */
  const ensureSaved = useCallback(async () => {
    if (readOnly) return true;
    clearTimeout(autoSaveTimerRef.current);
    for (let i = 0; i < 50 && savingRef.current; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => { setTimeout(r, 200); });
    }
    if (textRef.current === lastSavedRef.current) return true;
    return persistFlow({ silent: true });
  }, [persistFlow, readOnly]);

  useEffect(() => {
    if (loading || !text) return undefined;
    if (!loadedRef.current) {
      loadedRef.current = true;
      return undefined;
    }
    if (readOnly || saving || text === lastSavedText) return undefined;
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      persistFlow({ silent: true });
    }, 1500);
    return () => clearTimeout(autoSaveTimerRef.current);
  }, [text, loading, lastSavedText, persistFlow, readOnly, saving]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const saveRef = useRef(persistFlow);
  saveRef.current = persistFlow;
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveRef.current({ silent: false });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const openTab = (value) => {
    if (value === 'build' && tab !== 'build') setGraphEpoch((n) => n + 1);
    if (value === 'test') setTestMounted(true);
    if (ADVANCED_TABS.includes(value) && !advanced) {
      setAdvanced(true);
      try { window.localStorage.setItem(ADVANCED_STORAGE, '1'); } catch { /* ignore */ }
    }
    setTab(value);
  };

  const toggleAdvanced = (on) => {
    setAdvanced(on);
    try { window.localStorage.setItem(ADVANCED_STORAGE, on ? '1' : '0'); } catch { /* ignore */ }
    if (!on && ADVANCED_TABS.includes(tab)) openTab('build');
  };

  const onFixReadiness = (item) => {
    if (item.fix === 'json') openTab('json');
    else openTab('build');
  };

  const onDelete = async () => {
    if (!window.confirm('Delete this flow? This cannot be undone. Apps that use it will stop working.')) return;
    const { error: err } = await deleteV2Flow(id);
    if (err) {
      setError(err);
      return;
    }
    navigate('/studio/flows');
  };

  const onApplyTemplate = (tpl) => {
    setTemplateMenuAnchor(null);
    const label = FRIENDLY_FLOW_TEMPLATES[tpl.id]?.label || tpl.label;
    if (!window.confirm(`Replace the steps in this flow with the “${label}” template?`)) return;
    const { text: next, error: terr } = mergeTemplateIntoCurrentFlow(text, tpl.definition, tpl.id);
    if (terr) {
      setError(terr);
      return;
    }
    setText(next);
    setGraphEpoch((n) => n + 1);
    openTab('build');
    setToast(`Switched to the ${label} template`);
  };

  const onExportJson = () => {
    if (!def) {
      setError('The flow has a formatting problem, so it cannot be downloaded yet.');
      return;
    }
    const rawName = (def.name && String(def.name).trim()) || def.id || id || 'flow';
    const safe = String(rawName).replace(/[^\w-]+/g, '_').slice(0, 80) || 'flow';
    const blob = new Blob([JSON.stringify(def, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safe}.flow.v2.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onDuplicateFlow = async () => {
    if (!def) {
      setError('The flow has a formatting problem, so it cannot be copied yet.');
      return;
    }
    setDupBusy(true);
    setError(null);
    const { flow, error: err } = await createV2Flow({
      ...def,
      id: studioV2NewFlowDraft('temp').id,
      name: `Copy of ${flowNameOf(def)}`,
      schemaVersion: 'flow/v2',
    });
    setDupBusy(false);
    if (err) {
      setError(err);
      return;
    }
    if (flow?._id) navigate(`/studio/flows/${flow._id}`);
  };

  const onImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const inputEl = e.target;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const raw = JSON.parse(String(reader.result || '{}'));
        const v2 = raw.schemaVersion === 'flow/v2' ? raw : legacyFlowToV2Client(raw);
        const draft = studioV2NewFlowDraft(v2.name || 'Imported flow');
        let merged = { ...v2, id: draft.id, schemaVersion: 'flow/v2' };
        if (Array.isArray(merged.edges)) {
          merged = { ...merged, edges: merged.edges.map((edge) => normalizeV2FlowEdge(edge)) };
        }
        setImportBusy(true);
        setError(null);
        const { flow, error: cerr } = await createV2Flow(merged);
        setImportBusy(false);
        if (cerr) {
          setError(cerr);
          return;
        }
        if (flow?._id) navigate(`/studio/flows/${flow._id}`);
        else setError('Import failed: the server did not return a flow.');
      } catch (err) {
        setImportBusy(false);
        setError(err?.message || 'Import failed');
      } finally {
        inputEl.value = '';
      }
    };
    reader.readAsText(file);
  };

  const openAiAuthoringPrompt = async () => {
    setAiPromptError(null);
    setAiPromptText('');
    setAiPromptBusy(true);
    setAiPromptOpen(true);
    const { prompt, error: perr } = await getV2WorkflowBuilderPrompt(id);
    setAiPromptBusy(false);
    if (perr) setAiPromptError(perr);
    else setAiPromptText(prompt || '');
  };

  const copyAiAuthoringPrompt = async () => {
    try {
      await navigator.clipboard.writeText(aiPromptText);
      setToast('Prompt copied');
    } catch {
      setAiPromptError('Could not copy to the clipboard. Select the text and copy it manually.');
    }
  };

  const status = STATUS_CHIP[def?.status] || STATUS_CHIP.draft;
  const saveLabel = saving ? 'Saving…' : dirty ? 'Save changes' : 'All changes saved';

  const header = (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      {!loading && (
        <AppReadinessButton
          readiness={readiness}
          onFix={onFixReadiness}
          canEdit
          readyLabel="Ready to run"
          readyTitle="Everything needed is in place"
          pendingTitle="Before you run this flow"
          subtitle="These checks catch flows that would never start or would stop halfway."
          testId="flow-readiness"
        />
      )}
      {!loading && id && (
        <FlowAppsButton flowMongoId={id} flowName={flowNameOf(def)} readOnly={readOnly} onNotify={setToast} />
      )}
      <Button
        variant="text"
        color="inherit"
        onClick={() => persistFlow({ silent: false })}
        disabled={loading || saving || readOnly || !dirty}
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : (!dirty && <CheckIcon />)}
        sx={{
          textTransform: 'none', fontWeight: 600, color: 'text.secondary', minWidth: 160,
        }}
        data-testid="flow-save"
      >
        {saveLabel}
      </Button>
      <Button
        variant="outlined"
        startIcon={<AutoAwesomeIcon />}
        disabled={loading}
        onClick={() => setAssistantOpen((o) => !o)}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        Assistant
      </Button>
      <Button
        variant="contained"
        startIcon={<PlayArrowIcon />}
        disabled={loading}
        onClick={() => openTab('test')}
        sx={{ textTransform: 'none', fontWeight: 600 }}
        data-testid="flow-open-test"
      >
        Test
      </Button>
      <IconButton onClick={(e) => setActionsMenuAnchor(e.currentTarget)} disabled={loading} aria-label="More flow actions">
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={actionsMenuAnchor} open={Boolean(actionsMenuAnchor)} onClose={() => setActionsMenuAnchor(null)}>
        <MenuItem
          disabled={readOnly}
          onClick={(e) => {
            setActionsMenuAnchor(null);
            setTemplateMenuAnchor(e.currentTarget);
          }}
        >
          <ListItemIcon><ViewQuiltIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Start from a template…</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setActionsMenuAnchor(null); onDuplicateFlow(); }} disabled={dupBusy}>
          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setActionsMenuAnchor(null); onExportJson(); }}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download a copy</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setActionsMenuAnchor(null); importRef.current?.click(); }} disabled={importBusy}>
          <ListItemIcon><UploadFileIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Import a flow file</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setActionsMenuAnchor(null); openAiAuthoringPrompt(); }}>
          <ListItemIcon><SmartToyOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Prompt for an outside AI tool</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={readOnly}
          onClick={() => { setActionsMenuAnchor(null); onDelete(); }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete flow</ListItemText>
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={templateMenuAnchor}
        open={Boolean(templateMenuAnchor)}
        onClose={() => setTemplateMenuAnchor(null)}
        PaperProps={{ sx: { maxWidth: 420 } }}
      >
        {STUDIO_V2_FLOW_TEMPLATES.map((tpl) => {
          const copy = FRIENDLY_FLOW_TEMPLATES[tpl.id] || { label: tpl.label, description: tpl.description };
          return (
            <MenuItem key={tpl.id} onClick={() => onApplyTemplate(tpl)} sx={{ whiteSpace: 'normal' }}>
              <ListItemText primary={copy.label} secondary={copy.description} />
            </MenuItem>
          );
        })}
      </Menu>
      <input ref={importRef} type="file" accept="application/json,.json" hidden onChange={onImportFile} />
    </Stack>
  );

  const badJson = !loading && !def;

  return (
    <StudioPageShell
      title={(
        <Stack direction="row" spacing={1.25} alignItems="center" component="span">
          <span data-testid="flow-title">{loading ? 'Loading…' : flowNameOf(def)}</span>
          {!loading && def && (
            <Chip
              size="small"
              label={status.label}
              color={status.color}
              variant="outlined"
              sx={{ fontWeight: 600 }}
              data-testid="flow-status"
            />
          )}
        </Stack>
      )}
      subtitle={!loading && def?.description ? (
        <Box
          component="span"
          title={def.description}
          sx={{
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}
        >
          {def.description}
        </Box>
      ) : null}
      backTo="/studio/flows"
      backLabel="All flows"
      maxWidth={1680}
      actions={header}
    >
      <Stack spacing={2}>
        {!loading && readOnly && (
          <Alert
            severity="info"
            data-testid="flow-read-only"
            action={(
              <Button color="inherit" size="small" onClick={onDuplicateFlow} disabled={dupBusy}>
                {dupBusy ? 'Copying…' : 'Make my own copy'}
              </Button>
            )}
          >
            This flow belongs to another member of your workspace, so your changes here won&apos;t be saved.
            Make your own copy to edit it.
          </Alert>
        )}
        {saveError && !readOnly && (
          <Alert
            severity="error"
            data-testid="flow-save-error"
            action={(
              <Button color="inherit" size="small" onClick={() => persistFlow({ silent: false })} disabled={saving}>
                Retry
              </Button>
            )}
          >
            {saveError}
          </Alert>
        )}
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {badJson && tab !== 'json' && (
          <Alert
            severity="warning"
            action={<Button color="inherit" size="small" onClick={() => openTab('json')}>Open JSON</Button>}
          >
            The flow has a formatting problem, so the visual editor cannot show it. Fix it in the JSON view.
          </Alert>
        )}
        {loading ? <Loading /> : (
          <>
            <Stack direction="row" alignItems="center" sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tab}
                onChange={(_e, v) => openTab(v)}
                aria-label="Flow editor sections"
                variant="scrollable"
                scrollButtons="auto"
                sx={{ flex: 1, minWidth: 0, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 } }}
              >
                <Tab value="build" label="Build" />
                <Tab value="test" label="Test" />
                <Tab value="settings" label="Settings" />
                <Tab value="connections" label="Connections" />
                {advanced && <Tab value="edges" label="Edges" sx={{ color: 'text.secondary' }} />}
                {advanced && <Tab value="state" label="State" sx={{ color: 'text.secondary' }} />}
                {advanced && <Tab value="schema" label="State schema" sx={{ color: 'text.secondary' }} />}
                {advanced && <Tab value="json" label="JSON" sx={{ color: 'text.secondary' }} />}
              </Tabs>
              <Tooltip describeChild title="Show technical tools: connection table, stored data, data shape and raw JSON">
                <FormControlLabel
                  sx={{ ml: 1, mr: 0, flex: '0 0 auto' }}
                  control={<Switch size="small" checked={advanced} onChange={(e) => toggleAdvanced(e.target.checked)} />}
                  label={<Typography variant="body2" color="text.secondary">Advanced</Typography>}
                />
              </Tooltip>
            </Stack>

            <Box sx={{ display: tab === 'build' && def ? 'block' : 'none' }}>
              <Suspense fallback={<Loading />}>
                {def && <StudioV2FlowCanvas key={graphEpoch} initialText={text} onTextChange={setText} flowId={id} />}
              </Suspense>
            </Box>

            {testMounted && (
              <Box sx={{ display: tab === 'test' ? 'block' : 'none' }}>
                <FlowTestPanel
                  flowId={id}
                  def={def}
                  definitionText={text}
                  onBeforeRun={ensureSaved}
                  onNotify={setToast}
                  initialProfileId={debugProfileId}
                />
              </Box>
            )}

            {tab === 'settings' && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 }, borderRadius: 3, border: 1, borderColor: 'divider',
                }}
              >
                <StudioV2FlowSettingsEditor text={text} setText={setText} currentFlowMongoId={id} readOnly={readOnly} />
              </Paper>
            )}

            {tab === 'settings' && id && id !== 'new' && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 }, mt: 2, borderRadius: 3, border: 1, borderColor: 'divider',
                }}
              >
                <Section title="Triggers" description="Run this flow automatically from a webhook or on a schedule.">
                  <FlowTriggersPanel flowId={id} readOnly={readOnly} />
                </Section>
              </Paper>
            )}

            {tab === 'connections' && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 }, borderRadius: 3, border: 1, borderColor: 'divider',
                }}
              >
                <Stack spacing={5}>
                  <Section
                    title="Services and credentials"
                    description="Accounts and API keys this flow's steps can use. Secrets are stored encrypted and never shown again."
                  >
                    <Stack spacing={3}>
                      <StudioV2IntegrationCatalog text={text} setText={setText} />
                      <StudioV2ToolCatalog />
                    </Stack>
                  </Section>
                  <Divider />
                  <Section
                    title="Tool servers (MCP)"
                    description="Connect outside tool servers so AI steps in this flow can call their tools."
                  >
                    <StudioV2FlowMcpEditor text={text} setText={setText} />
                  </Section>
                </Stack>
              </Paper>
            )}

            {tab === 'edges' && <StudioV2FlowEdgesTable text={text} setText={setText} />}
            {tab === 'state' && <StudioV2FlowStateEditor text={text} setText={setText} />}
            {tab === 'schema' && <StudioV2FlowStateSchemaEditor text={text} setText={setText} />}
            <Box sx={{ display: tab === 'json' ? 'block' : 'none' }}>
              <StudioV2JsonMonacoEditor value={text} onChange={setText} schemaKind="flow" active={tab === 'json'} />
            </Box>
          </>
        )}
      </Stack>

      <Drawer
        anchor="right"
        open={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 460 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column',
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider',
          }}
        >
          <AutoAwesomeIcon color="primary" sx={{ mr: 1 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>Flow assistant</Typography>
            <Typography variant="caption" color="text.secondary">Describe what to build or change.</Typography>
          </Box>
          <IconButton aria-label="Close assistant" onClick={() => setAssistantOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box sx={{
          flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 1.5,
        }}
        >
          {assistantOpen && (
            <Suspense fallback={<Loading />}>
              <StudioV2FlowAiChatPanel
                flowMongoId={id}
                definitionText={text}
                fillHeight
                initialPrompt={assistantPrompt}
                onApplyDefinition={(json) => {
                  setText(json);
                  setGraphEpoch((n) => n + 1);
                  setAssistantPrompt('');
                  openTab('build');
                  setToast('Applied the assistant’s version. It saves automatically.');
                }}
              />
            </Suspense>
          )}
        </Box>
      </Drawer>

      <Dialog open={aiPromptOpen} onClose={() => !aiPromptBusy && setAiPromptOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Prompt for an outside AI tool
          <Typography variant="body2" color="text.secondary">
            Paste this into another AI assistant so it can write flows in the right format. For changes inside Studio, use the Assistant button instead.
          </Typography>
        </DialogTitle>
        <DialogContent>
          {aiPromptBusy && <Loading />}
          {aiPromptError && <Alert severity="error" sx={{ mb: 1 }}>{aiPromptError}</Alert>}
          {!aiPromptBusy && !aiPromptError && (
            <TextField
              value={aiPromptText}
              multiline
              fullWidth
              minRows={14}
              maxRows={28}
              InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiPromptOpen(false)} disabled={aiPromptBusy}>Close</Button>
          <Button variant="contained" onClick={copyAiAuthoringPrompt} disabled={aiPromptBusy || !aiPromptText}>
            Copy
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </StudioPageShell>
  );
}

export default StudioFlowEditPage;
